/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AppLogo,
  ConfirmModal,
  SupportConfirmModal,
  Storage,
} from "@advapacs/common";
import { useSelector } from "react-redux";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { IRootState } from "../config/reducers";
import i18n from "../services/i18n";
import { sidebarAction } from "../reducers/slices/sidebarSlice";
import { logout, refreshToken } from "../pages/login/ducks/operations";
import { useAppDispatch } from "../config/store";
import { NotificationWrapper } from "../components/Notification";
import NotificationPage from "../components/Notification/NotificationPage";
import { NotificationAction } from "../components/Notification/ducks/notificationSlice";
import {
  dismissAllNotification,
  getNotificationCount,
} from "../components/Notification/ducks/operations";
import useRegionDropdown from "../hooks/useRegionDropdown";
import { OverlayInjectedProps } from "react-bootstrap/Overlay";
import { DOCS_URL, SUPPORT_URL } from "../config/constants";

interface UserHeaderMenuProps {
  isSidebar: boolean;
}

export const UserHeaderMenu: FC<UserHeaderMenuProps> = ({ isSidebar }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { account } = useSelector((state: IRootState) => state.loginReducer);
  const { preventLogout, messagePrevent } = useSelector(
    (state: IRootState) => state.sidebarReducer
  );
  const { intervalSeconds, fetchCount, count } = useSelector(
    (state: IRootState) => state.notificationReducer
  );
  const intervalIdRef = useRef<NodeJS.Timeout>(undefined);
  const [selectedRegion, setSelectedRegion] = useState(undefined);
  const [countValue, setCountValue] = useState(count);
  const regions = useRegionDropdown();
  const [showNavigateConfirmModal, setShowNavigateConfirmModal] =
    useState<boolean>(false);
  const [showNotificationModal, setShowNotificationModal] =
    useState<boolean>(false);
  const [showSupportModal, setShowSupportModal] = useState<boolean>(false);
  const onLogoutHandle = useCallback(async () => {
    await i18n.changeLanguage("en");
    Storage.session.remove("locale");
    Storage.session.remove("study_filter");
    Storage.local.remove("path");
    localStorage.removeItem("region");
    dispatch(logout());
  }, [dispatch]);

  const fetchData = React.useCallback(() => {
    dispatch(getNotificationCount());
  }, [dispatch]);

  const storageFunction = (event: StorageEvent) => {
    if (event.key === "region" && event.newValue) {
      // @ts-ignore
      setSelectedRegion(event.newValue);
    }
  };

  // DidMount
  useEffect(() => {
    fetchData();
    window.addEventListener("storage", storageFunction);
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
      dispatch(NotificationAction.clearVariableCountNotification());
      window.removeEventListener("storage", storageFunction);
    };
  }, []);

  useEffect(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
    }
    if (intervalSeconds > 0) {
      intervalIdRef.current = setInterval(() => {
        if (fetchCount === 0) {
          fetchData();
        }
      }, intervalSeconds * 1000);
    }
  }, [fetchData, fetchCount, intervalSeconds]);

  useEffect(() => {
    if (selectedRegion !== localStorage.getItem("region") && selectedRegion) {
      localStorage.setItem("region", selectedRegion);
      refreshToken(selectedRegion)
        .then(() => {})
        .finally(() => {
          setTimeout(() => {
            window.location.reload();
            // redirect 2FA it if require !!!
          }, 100);
        });
    }
  }, [selectedRegion]);

  useEffect(() => {
    if (!selectedRegion) {
      // @ts-ignore
      setSelectedRegion(localStorage.getItem("region"));
    }
  }, []);

  useEffect(() => {
    setCountValue(count);
  }, [count]);

  const showNameRegion = (selected: string) => {
    if (!selected) return "";
    return regions?.find((x) => x.value === selected)?.label;
  };

  const handlePreventLogout = () => {
    if (preventLogout) {
      setShowNavigateConfirmModal(true);
    } else {
      onLogoutHandle();
    }
  };

  const handleHideNotification = () => {
    setShowNotificationModal(!showNotificationModal);
    if (!showNotificationModal) {
      dispatch(NotificationAction.clearNotification());
    }
  };

  const handleDismissAllNotification = () => {
    if (countValue <= 0) return;
    setShowNotificationModal(!showNotificationModal);
    dispatch(dismissAllNotification());
    setCountValue(0);
  };

  const renderTooltip = (propTooltip: OverlayInjectedProps, title: string) => (
    <Tooltip id="button-tooltip" {...propTooltip}>
      {title}
    </Tooltip>
  );

  const openDocLink = () => {
    window.open(`${DOCS_URL}`, "_blank");
  };

  return (
    <>
      <div className="row flex-center-start">
        {showNavigateConfirmModal && (
          <ConfirmModal
            show={showNavigateConfirmModal}
            onClose={() => setShowNavigateConfirmModal(false)}
            body={messagePrevent}
            cancel={() => setShowNavigateConfirmModal(false)}
            confirm={onLogoutHandle}
          />
        )}
        {showSupportModal && (
          <SupportConfirmModal
            showModal={showSupportModal}
            onConfirm={() => {
              window.open(`${SUPPORT_URL}`, "_blank");
              setShowSupportModal(false);
            }}
            onCancel={() => setShowSupportModal(false)}
          />
        )}
        <Dropdown className="nav-item dropdown region-switcher">
          <Dropdown.Toggle
            id="dropdown-basic"
            className="dropdown-customer-pacs btn btn-light"
          >
            <span>{showNameRegion(selectedRegion as unknown as string)}</span>
          </Dropdown.Toggle>
          {regions?.length > 0 && (
            <Dropdown.Menu className="dropdown-menu dropdown-menu-right">
              {regions.map((item) => {
                return (
                  <Dropdown.Item
                    key={item.value}
                    onClick={() => {
                      if (selectedRegion !== item.value)
                        setSelectedRegion(item.value);
                    }}
                  >
                    <span>{item.label}</span>
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          )}
        </Dropdown>
        <div className="support-docs mr-2 cursor-pointer">
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={(props) => renderTooltip(props, t("support_tooltip"))}
          >
            <i
              className="fas fa-headset"
              onClick={() => {
                setShowSupportModal(true);
              }}
            />
          </OverlayTrigger>
        </div>
        <div className="support-docs ml-2 cursor-pointer">
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={(props) => renderTooltip(props, t("docs_tooltip"))}
          >
            <i className="fas fa-book" onClick={openDocLink} />
          </OverlayTrigger>
        </div>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleHideNotification();
          }}
          className="navbar-nav-link navbar-nav-link-icon rounded-pill"
        >
          <i className="fa fa-bell"></i>
          <span className="badge bg-primary text-black position-absolute top-0 end-0 translate-middle-top zindex-1 rounded-pill mt-1 me-1">
            {countValue}
          </span>
        </a>
        <Dropdown className="nav-item dropdown custom-dropdown">
          <Dropdown.Toggle
            id="dropdown-basic"
            className={`${isSidebar ? "text-white" : ""}`}
          >
            <i className="icon-user" />
            &nbsp;
            <span>{account?.username}</span>
          </Dropdown.Toggle>
          <Dropdown.Menu className="dropdown-menu dropdown-menu-right">
            <Dropdown.Item onClick={() => navigate("/UserSettings")}>
              <i className="icon-cog3" />
              <span>{t("common:settings")}</span>
            </Dropdown.Item>
            <Dropdown.Item
              className="dropdown-item"
              onClick={() => {
                handlePreventLogout();
              }}
            >
              <i className="icon-switch2" />
              <span>{t("login:logout")}</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
      {showNotificationModal && (
        <NotificationWrapper
          show={showNotificationModal}
          hideFilter={handleHideNotification}
          dismissAll={handleDismissAllNotification}
        >
          <>
            <div className="form-group">
              <NotificationPage />
            </div>
          </>
        </NotificationWrapper>
      )}
    </>
  );
};

interface IHeader {
  toggleSidebar?: () => void | undefined;
  expandSidebar?: boolean;
}

const Header = (props: IHeader): ReactElement => {
  const { secondarySidebar, expand } = useSelector(
    (state: IRootState) => state.sidebarReducer
  );
  const { isMobile } = useSelector((state: IRootState) => state.layoutSlice);

  const [expandSidebar, setExpandSidebar] = useState(false);
  useEffect(() => {
    setExpandSidebar(expand);
  }, [expand]);

  const [showMobileNavbar, setShowMobileNavbar] =
    useState<boolean>(secondarySidebar);
  const dispatch = useAppDispatch();
  const { toggleSidebar } = props;

  useEffect(() => {
    setShowMobileNavbar(secondarySidebar);
  }, [secondarySidebar]);

  return (
    <>
      <div className="navbar navbar-header navbar-expand-lg navbar-light fixed-top navbar-static">
        <div
          className={`navbar-brand ${
            expandSidebar
              ? "navbar-brand-md text-lg-left"
              : "navbar-brand-xs text-center"
          }`}
        >
          <Link to="/" className="d-inline-block">
            {expandSidebar && !isMobile ? (
              <AppLogo.LogoLong />
            ) : (
              <AppLogo.LogoShort />
            )}
          </Link>
        </div>
        <div className="d-flex flex-1 d-lg-none flex-end">
          <button
            onClick={() => {
              dispatch(sidebarAction.toggleSidebarSecondary(!showMobileNavbar));
              setShowMobileNavbar(!showMobileNavbar);
            }}
            className="navbar-toggler"
            type="button"
            data-toggle="collapse"
            data-target="#navbar-mobile"
          >
            <i className="icon-tree5" />
          </button>
          {toggleSidebar && (
            <button
              className="navbar-toggler sidebar-mobile-main-toggle"
              type="button"
              onClick={toggleSidebar}
            >
              <i className="icon-paragraph-justify3" />
            </button>
          )}
        </div>
        {toggleSidebar && !isMobile && (
          <div
            className={`collapse navbar-collapse ${isMobile ? "show" : ""}`}
            id="navbar-mobile"
          >
            <ul className="navbar-nav ">
              <li className="nav-item">
                <div
                  className="navbar-nav-link sidebar-control sidebar-main-toggle d-none d-md-block"
                  onClick={toggleSidebar}
                >
                  <i className="icon-paragraph-justify3" />
                </div>
              </li>
            </ul>
            <ul className="navbar-nav ml-auto flex-center-start">
              <UserHeaderMenu isSidebar={false} />
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default Header;
